/**
 * The link picker inside the task drawer (DD-052, never a second dialog,
 * DD-051): one tab per type, a search that waits 250 ms after the last
 * keystroke, and up to 20 of the client's items. Items the task already
 * links are marked and disabled.
 */

import React, { useEffect, useMemo, useState } from "react";
import { SpinnerIcon } from "@/components/icons";
import { PanelFilterOption, PanelFilterTabs } from "@/components/ui/PanelFilterTabs";
import { cn } from "@/utils/cn";
import { useTaskLinkOptionsQuery } from "../hooks/useTasks";
import type { TaskLinkOption, TaskLinkRef, TaskLinkType } from "../types";
import { LINK_PICKER_TYPES, LINK_SEARCH_DEBOUNCE_MS, linkEntity, markLinkedOptions } from "./taskLinks";

interface TaskLinkPickerProps {
  clientId: string;
  /** The task's direct links. */
  links: TaskLinkRef[];
  /** A pick is being saved: rows wait. */
  pending: boolean;
  onPick: (option: TaskLinkOption) => void;
  onCancel: () => void;
}

const TABS: PanelFilterOption[] = LINK_PICKER_TYPES.map((type) => ({
  id: type,
  label: linkEntity(type).plural,
}));

function isLinkType(id: string): id is TaskLinkType {
  return LINK_PICKER_TYPES.some((type) => type === id);
}

export function TaskLinkPicker({ clientId, links, pending, onPick, onCancel }: TaskLinkPickerProps) {
  const [type, setType] = useState<TaskLinkType>("idea");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search.trim()), LINK_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  const { data: options, isLoading, isError, isPlaceholderData } = useTaskLinkOptionsQuery(
    clientId,
    type,
    query
  );
  const rows = useMemo(() => markLinkedOptions(options ?? [], links), [options, links]);

  let results: React.ReactNode;
  if (isLoading) {
    results = (
      <div className="flex justify-center py-3">
        <SpinnerIcon className="w-5 h-5 text-gray-400" />
      </div>
    );
  } else if (isError) {
    results = <p className="text-sm text-red-600">Impossibile caricare gli elementi.</p>;
  } else if (rows.length === 0) {
    results = <p className="text-sm text-gray-400">Nessun risultato.</p>;
  } else {
    results = (
      <ul
        className={cn("divide-y divide-gray-100", isPlaceholderData && "opacity-60 pointer-events-none")}
        aria-busy={isPlaceholderData}
      >
        {rows.map((option) => {
          const Icon = linkEntity(option.type).icon;
          return (
            <li key={`${option.type}:${option.id}`}>
              <button
                type="button"
                disabled={option.linked || pending || isPlaceholderData}
                onClick={() => onPick(option)}
                className="w-full flex items-center gap-2 py-2 text-left disabled:cursor-default enabled:hover:bg-gray-50"
              >
                <Icon className="w-4 h-4 shrink-0 text-gray-400" />
                <span className="min-w-0 flex-1">
                  <span
                    className={cn("block truncate text-sm", option.linked ? "text-gray-400" : "text-gray-800")}
                  >
                    {option.title}
                  </span>
                  {option.subtitle && (
                    <span className="block truncate text-xs text-gray-400">{option.subtitle}</span>
                  )}
                </span>
                {option.linked && <span className="shrink-0 text-xs text-gray-400">Collegato</span>}
              </button>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div className="space-y-3">
      <PanelFilterTabs
        options={TABS}
        activeId={type}
        onChange={(id) => {
          if (isLinkType(id)) setType(id);
        }}
      />
      <input
        type="search"
        autoFocus
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Cerca…"
        aria-label="Cerca un elemento da collegare"
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
      {results}
      <button type="button" onClick={onCancel} className="text-sm text-gray-500 hover:text-gray-700">
        Annulla
      </button>
    </div>
  );
}
